import { type ReactElement, useEffect, useState } from 'react';
import { s } from '@/theme/tokens';
import { Box, Panel, Stack, Text } from '@/ui';
import { settingsPanelSurfaceSx } from '@/ui/composites/settingsPageTypographySx';
import { Button } from '@/ui/primitives/Button';
import { Switch } from '@/ui/primitives/Switch';
import type { useUpdateProject } from '@/features/projects';
import type { ProjectDto } from '@flowqa/shared';

type MutationErrorController = { clearError: () => void; setError: (err: unknown) => void };

type NotifyInput = { message: string; tone: 'green' | 'blue' | 'red' };

export type ProjectTestDataControlsSectionProps = {
  project: ProjectDto | null;
  updateProject: ReturnType<typeof useUpdateProject>;
  mutationError: MutationErrorController;
  notify: (input: NotifyInput) => void;
};

export const ProjectTestDataControlsSection = ({
  project,
  updateProject,
  mutationError,
  notify,
}: ProjectTestDataControlsSectionProps): ReactElement => {
  const controlDescriptionTextSx = {
    color: 'text.secondary',
    maxWidth: '62ch',
  } as const;

  const [retainDiagnosticOutput, setRetainDiagnosticOutput] = useState(
    () => project?.testDataControls.retainDiagnosticOutput ?? false,
  );
  const [includeResultsFileMetadata, setIncludeResultsFileMetadata] = useState(
    () => !(project?.testDataControls.strictDataMinimisation ?? true),
  );
  const [groupErroredWithFailed, setGroupErroredWithFailed] = useState(
    () => project?.testDataControls.groupErroredWithFailed ?? true,
  );

  useEffect(() => {
    if (!project) {
      return;
    }
    setRetainDiagnosticOutput(project.testDataControls.retainDiagnosticOutput);
    setIncludeResultsFileMetadata(!project.testDataControls.strictDataMinimisation);
    setGroupErroredWithFailed(project.testDataControls.groupErroredWithFailed);
  }, [project]);

  const isDirty =
    project !== null &&
    (retainDiagnosticOutput !== project.testDataControls.retainDiagnosticOutput ||
      includeResultsFileMetadata !== !project.testDataControls.strictDataMinimisation ||
      groupErroredWithFailed !== project.testDataControls.groupErroredWithFailed);

  return (
    <Panel title="Test Configuration" surfaceVariant="settings" sx={settingsPanelSurfaceSx}>
      <Stack gap="lg">
        <Stack gap="md">
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1fr) auto',
              columnGap: s('md'),
              alignItems: 'center',
            }}
          >
            <Stack gap="xxs">
              <Text role="body">Count Errored Status As Failed</Text>
              <Text role="bodyMuted" sx={controlDescriptionTextSx}>
                Group errored runs with failures in summaries, thresholds, and breakdowns.
              </Text>
            </Stack>
            <Switch
              size="small"
              density="compact"
              checked={groupErroredWithFailed}
              disabled={!project || updateProject.isPending}
              onChange={(_event, checked) => {
                setGroupErroredWithFailed(checked);
              }}
            />
          </Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1fr) auto',
              columnGap: s('md'),
              alignItems: 'center',
            }}
          >
            <Stack gap="xxs">
              <Text role="body">Preserve Results File Output</Text>
              <Text role="bodyMuted" sx={controlDescriptionTextSx}>
                Store parsed stdout, stderr, and failure output from automated results files.
              </Text>
            </Stack>
            <Switch
              size="small"
              density="compact"
              checked={retainDiagnosticOutput}
              disabled={!project || updateProject.isPending}
              onChange={(_event, checked) => {
                setRetainDiagnosticOutput(checked);
              }}
            />
          </Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1fr) auto',
              columnGap: s('md'),
              alignItems: 'center',
            }}
          >
            <Stack gap="xxs">
              <Text role="body">Include Results File Metadata</Text>
              <Text role="bodyMuted" sx={controlDescriptionTextSx}>
                Store additional metadata parsed from uploaded results files.
              </Text>
            </Stack>
            <Switch
              size="small"
              density="compact"
              checked={includeResultsFileMetadata}
              disabled={!project || updateProject.isPending}
              onChange={(_event, checked) => {
                setIncludeResultsFileMetadata(checked);
              }}
            />
          </Box>
        </Stack>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: s('sm') }}>
          <Button
            variant="ghost"
            fullWidthOnMobile={false}
            disabled={!isDirty || updateProject.isPending}
            onClick={() => {
              if (!project) {
                return;
              }
              setRetainDiagnosticOutput(project.testDataControls.retainDiagnosticOutput);
              setIncludeResultsFileMetadata(!project.testDataControls.strictDataMinimisation);
              setGroupErroredWithFailed(project.testDataControls.groupErroredWithFailed);
              mutationError.clearError();
            }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            fullWidthOnMobile={false}
            disabled={!isDirty || updateProject.isPending}
            loading={updateProject.isPending}
            onClick={() => {
              if (!project) {
                return;
              }
              updateProject.mutate(
                {
                  id: project.id,
                  testDataControls: {
                    retainDiagnosticOutput,
                    strictDataMinimisation: !includeResultsFileMetadata,
                    groupErroredWithFailed,
                  },
                },
                {
                  onSuccess: () => {
                    mutationError.clearError();
                    notify({ message: 'Updated test configuration', tone: 'green' });
                  },
                  onError: (error) => {
                    mutationError.setError(error);
                  },
                },
              );
            }}
          >
            Update
          </Button>
        </Box>
      </Stack>
    </Panel>
  );
};
